import { createContext, useContext, useEffect } from 'react';

import { PopoverProps, useTheme } from '@mui/material';

import { Select, Typography, Box } from '@cast/design-system';
import { WorkloadFilterLabelValue } from '@cast/types';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';

import { useLabelsFilterDrawer } from './_hooks/useLabelsFilterDrawer';
import { LabelGroup } from './_types';
import { toBeModel, toUiModel } from './_utils';

import { useSearchCriterion } from '@cast/shared-search-ui';

const FilterContext = createContext<{ onApply?: () => void }>({});

const PopoverComponent = ({ open, onClose }: PopoverProps) => {
  const { currentDateRange } = useCostReportContext();
  const { onApply } = useContext(FilterContext);

  const labelsCriterion = useSearchCriterion('labels');
  const selectedLabels: WorkloadFilterLabelValue[] = labelsCriterion?.value;

  const { openDrawer } = useLabelsFilterDrawer({
    onClose: () => {
      onClose?.({}, 'backdropClick');
    },
    labelsSelected: (labels?: LabelGroup[]) => {
      if (Array.isArray(labels) && labels?.length) {
        labelsCriterion.setValue?.(toBeModel(labels));
        onApply?.();
      } else {
        labelsCriterion.clear?.();
      }
    },
    range: currentDateRange,
  });

  useEffect(() => {
    if (open) {
      openDrawer(selectedLabels ? toUiModel(selectedLabels) : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return null;
};

type Props = {
  onOpen?: () => void;
  onApply?: () => void;
  size?: 'sm' | 'md' | 'lg';
};

export const LabelsFilter = ({ onApply, onOpen, size }: Props) => {
  const { palette } = useTheme();
  const value = [];

  const labelsCriterion = useSearchCriterion('labels');
  const selectedLabels = labelsCriterion?.value;

  return (
    <FilterContext.Provider value={{ onApply }}>
      <Select
        key={JSON.stringify(selectedLabels || {})}
        sx={{
          '.DS-Dropdown-arrow': {
            display: 'none',
          },
          '.DS-Dropdown-multiValueCount': {
            display: 'none!important',
          },
          ...(!value?.length && {
            '.DS-Dropdown-multiValueText': {
              color: `${palette.grey[500]}!important`,
            },
          }),
        }}
        inputText="Labels"
        options={selectedLabels || []}
        value={selectedLabels || []}
        multiple
        size={size}
        endAdornment={
          selectedLabels?.length ? (
            <Box
              sx={{
                minWidth: 25,
                height: 20,
                background: palette.grey[600],
                color: '#fff',
                typography: 'P14M',
                textAlign: 'center',
                borderRadius: '40px',
                lineHeight: '20px',
              }}
            >
              {selectedLabels.length}
            </Box>
          ) : (
            <Typography variant="inherit" color="blue.500">
              Select
            </Typography>
          )
        }
        onOpen={onOpen}
        PopoverComponent={PopoverComponent}
        testId="labels-filter"
      />
    </FilterContext.Provider>
  );
};
